"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades + 12px-slide its children into view the first time they
 * intersect the viewport, then never re-triggers. Respects prefers-reduced-
 * motion (CSS @media block in globals.css strips the transform).
 *
 * Intentionally tiny and dependency-free — Round 3 motion budget is strict.
 */
export function Reveal({
  children,
  delayMs = 0,
  className = "",
}: {
  children: React.ReactNode;
  delayMs?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [armed, setArmed] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If the platform lacks IO, skip the animation entirely — content stays
    // visible (the default in CSS) and we never arm the hidden state.
    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return;
    }
    // Arm the hidden state on the client only, so SSR/no-JS/crawlers/
    // screenshot bots see fully visible content.
    setArmed(true);
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            window.setTimeout(() => setRevealed(true), delayMs);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "-10% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delayMs]);

  return (
    <div
      ref={ref}
      data-armed={armed ? "true" : "false"}
      data-revealed={revealed ? "true" : "false"}
      className={`reveal ${className}`}
    >
      {children}
    </div>
  );
}
